import * as React from 'react';
import type { JSX } from 'react';

import { AlertDialog as AlertDialogPrimitive } from 'radix-ui';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogMedia,
  AlertDialogPortal,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';

const UsersAlertDialog = (props: React.ComponentProps<typeof AlertDialog>): JSX.Element => <AlertDialog {...props} />;
UsersAlertDialog.displayName = 'UsersAlertDialog';

const UsersAlertDialogTrigger = (props: React.ComponentProps<typeof AlertDialogTrigger>): JSX.Element => (
  <AlertDialogTrigger {...props} />
);
UsersAlertDialogTrigger.displayName = 'UsersAlertDialogTrigger';

const UsersAlertDialogPortal = (props: React.ComponentProps<typeof AlertDialogPortal>): JSX.Element => (
  <AlertDialogPortal {...props} />
);
UsersAlertDialogPortal.displayName = 'UsersAlertDialogPortal';

const UsersAlertDialogOverlay = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Overlay
    ref={ref}
    data-slot="alert-dialog-overlay"
    className={cn(
      'fixed inset-0 z-50 bg-black/10 duration-100 supports-backdrop-filter:backdrop-blur-xs data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0',
      className
    )}
    {...props}
  />
));
UsersAlertDialogOverlay.displayName = 'UsersAlertDialogOverlay';

const UsersAlertDialogContent = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Content> & {
    size?: 'default' | 'sm';
  }
>(({ className, size = 'default', ...props }, ref) => (
  <AlertDialogPortal>
    <UsersAlertDialogOverlay />
    <AlertDialogPrimitive.Content
      ref={ref}
      data-slot="alert-dialog-content"
      data-size={size}
      className={cn(
        'group/alert-dialog-content fixed top-1/2 left-1/2 z-50 grid w-full -translate-x-1/2 -translate-y-1/2 gap-4 rounded-xl bg-popover p-4 text-popover-foreground ring-1 ring-foreground/10 duration-100 outline-none data-[size=default]:max-w-xs data-[size=sm]:max-w-xs data-[size=default]:sm:max-w-sm data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95',
        className
      )}
      {...props}
    />
  </AlertDialogPortal>
));
UsersAlertDialogContent.displayName = 'UsersAlertDialogContent';

const UsersAlertDialogHeader = (props: React.ComponentProps<typeof AlertDialogHeader>): JSX.Element => (
  <AlertDialogHeader {...props} />
);
UsersAlertDialogHeader.displayName = 'UsersAlertDialogHeader';

const UsersAlertDialogFooter = (props: React.ComponentProps<typeof AlertDialogFooter>): JSX.Element => (
  <AlertDialogFooter {...props} />
);
UsersAlertDialogFooter.displayName = 'UsersAlertDialogFooter';

const UsersAlertDialogMedia = (props: React.ComponentProps<typeof AlertDialogMedia>): JSX.Element => (
  <AlertDialogMedia {...props} />
);
UsersAlertDialogMedia.displayName = 'UsersAlertDialogMedia';

const UsersAlertDialogTitle = (props: React.ComponentProps<typeof AlertDialogTitle>): JSX.Element => (
  <AlertDialogTitle {...props} />
);
UsersAlertDialogTitle.displayName = 'UsersAlertDialogTitle';

const UsersAlertDialogDescription = (props: React.ComponentProps<typeof AlertDialogDescription>): JSX.Element => (
  <AlertDialogDescription {...props} />
);
UsersAlertDialogDescription.displayName = 'UsersAlertDialogDescription';

const UsersAlertDialogAction = (props: React.ComponentProps<typeof AlertDialogAction>): JSX.Element => (
  <AlertDialogAction {...props} />
);
UsersAlertDialogAction.displayName = 'UsersAlertDialogAction';

const UsersAlertDialogCancel = (props: React.ComponentProps<typeof AlertDialogCancel>): JSX.Element => (
  <AlertDialogCancel {...props} />
);
UsersAlertDialogCancel.displayName = 'UsersAlertDialogCancel';

export {
  UsersAlertDialog,
  UsersAlertDialogAction,
  UsersAlertDialogCancel,
  UsersAlertDialogContent,
  UsersAlertDialogDescription,
  UsersAlertDialogFooter,
  UsersAlertDialogHeader,
  UsersAlertDialogMedia,
  UsersAlertDialogOverlay,
  UsersAlertDialogPortal,
  UsersAlertDialogTitle,
  UsersAlertDialogTrigger,
};
